/**
 * @providesModule LiveBracketTrackingHelper
 */

import CurrentUserStore from 'CurrentUserStore';
import Tournament from 'SKZTournament';
import SkillzModule from 'SkillzModule';

/**
 * Interface for base live bracket tracking data
 */
interface BaseLiveBracketData {
  total_number_player_needed: number;
  bracket_id: string;
  players_filled: number;
  bracket_entry_fee: number;
  game_id: string;
  wallet_balance: number;
  prize_pool: string;
  registration_start_timestamp: string;
}

/**
 * Generates consistent tracking data for live bracket events
 * @param tournament - The tournament object
 * @param additionalData - Any additional data to merge with base data
 * @returns JSON stringified tracking data
 */
const getLiveBracketTrackingData = (
  tournament: Tournament,
  additionalData: Record<string, any> = {}
): string => {
  const currentUser = CurrentUserStore.getCurrentCachedUserData();

  const baseData: BaseLiveBracketData = {
    total_number_player_needed: tournament.max_players || 0,
    bracket_id: tournament.bracket?.id || tournament.id || '',
    players_filled: tournament.bracket?.num_entries || 0,
    bracket_entry_fee: tournament.getCashEntryFee() || 0,
    game_id: SkillzModule.App.GameId || '',
    wallet_balance: currentUser?.cash_balance || 0,
  };

  const finalData = {...baseData, ...additionalData};
  const jsonResult = JSON.stringify(finalData);

  return jsonResult;
};

/**
 * Generates consistent tracking data for live bracket events
 * @param tournament - The tournament object
 * @param additionalData - Any additional data to merge with base data
 * @returns object with tracking data
 */
const getLiveBracketTrackingDataV2 = (tournament: Tournament): Record<string, any> => {
  const currentUser = CurrentUserStore.getCurrentCachedUserData();

  const baseData: BaseLiveBracketData = {
    total_number_player_needed:tournament?.bracket?.size || 0,
    bracket_id: tournament?.bracket?.id || tournament?.id || '',
    players_filled: tournament?.bracket?.num_entries || 0,
    bracket_entry_fee: tournament?.getCashEntryFee?.() || 0,
    game_id: SkillzModule.App.GameId || '',
    wallet_balance: currentUser?.cash_balance || 0,
    prize_pool:
      tournament
        ?.mapPrizesToCurrency?.()
        ?.map((prize) => `${prize?.amount} ${prize?.type}`)
        ?.join?.(', ') || '',
    registration_start_timestamp: tournament?.bracket?.register_start_time || '',
  };

  return baseData;
};

/**
 * Generates permission flag tracking data
 * @param flagValue - 1 for granted/success, 0 for denied/failed
 * @param flagType - Type of permission ('location_permission_flag', 'calendar_permission_flag', etc.)
 * @returns JSON stringified permission data
 */
const getPermissionTrackingData = (flagValue: 0 | 1, flagType: string): string => {
  const permissionData = {
    [flagType]: flagValue,
  };
  const jsonResult = JSON.stringify(permissionData);

  return jsonResult;
};

/**
 * Generates result tracking data for success/failure events
 * @param success - true for success, false for failure
 * @returns JSON stringified result data
 */
const getResultTrackingData = (success: boolean): string => {
  const resultData = {
    success_flag: success ? 1 : 0,
  };
  const jsonResult = JSON.stringify(resultData);

  return jsonResult;
};

/**
 * Live Bracket Tracking Helper
 * Provides consistent tracking data generation for all live bracket events
 */
const LiveBracketTrackingHelper = {
  getLiveBracketTrackingData,
  getLiveBracketTrackingDataV2,
  getPermissionTrackingData,
  getResultTrackingData,
};

export default LiveBracketTrackingHelper;
